import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useAuth } from "@/context/AuthContext";
import { useSavedJobs } from "@/hooks/useSavedJobs";
import { supabase } from "@/lib/supabase";
import AnimatedModal from "@/components/base/AnimatedModal";

interface JobRow {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  is_hot: boolean;
  description: string;
  posted_at: string;
  posted_by: string;
  employer_email: string | null;
  employer_phone: string | null;
}

interface SavedJobCard {
  id: string;
  title: string;
  company: string;
  location: string;
  salary: string;
  type: string;
  isHot: boolean;
  description: string;
  postedAt: string;
  postedBy: string;
  employerEmail: string | null;
  employerPhone: string | null;
  savedAt: string;
}

function mapJobRow(row: JobRow, savedAt: string): SavedJobCard {
  return {
    id: row.id,
    title: row.title,
    company: row.company,
    location: row.location,
    salary: row.salary,
    type: row.type,
    isHot: row.is_hot,
    description: row.description,
    postedAt: row.posted_at,
    postedBy: row.posted_by,
    employerEmail: row.employer_email,
    employerPhone: row.employer_phone,
    savedAt,
  };
}

const typeLabels: Record<string, string> = {
  "full-time": "Toàn thời gian",
  "part-time": "Bán thời gian",
  contract: "Hợp đồng",
};

export default function SavedJobsPage() {
  const { user, isAuthenticated } = useAuth();
  const { savedJobs, savedCount, loading: savedLoading, toggleSave, isSaved, fetchSavedJobs } = useSavedJobs();
  const [jobs, setJobs] = useState<SavedJobCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch full job details for saved jobs
  const fetchJobDetails = useCallback(async () => {
    if (!isAuthenticated || savedJobs.length === 0) {
      setJobs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const jobIds = savedJobs.map((s) => s.job_id);

      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_jobs")
        .select("id, title, company, location, salary, type, is_hot, description, posted_at, posted_by, employer_email, employer_phone")
        .in("id", jobIds);

      if (supabaseError) {
        setError("Không thể tải dữ liệu việc làm. Vui lòng thử lại.");
        return;
      }

      if (data) {
        // Create a map of job_id → saved_at for ordering
        const savedAtMap = new Map(savedJobs.map((s) => [s.job_id, s.created_at]));

        const mapped = (data as JobRow[])
          .map((row) => mapJobRow(row, savedAtMap.get(row.id) || ""))
          // Sort by saved date (most recent first)
          .sort((a, b) => b.savedAt.localeCompare(a.savedAt));

        setJobs(mapped);
      }
    } catch {
      setError("Không thể tải dữ liệu việc làm. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, savedJobs]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const handleUnsave = async (jobId: string) => {
    await toggleSave(jobId);
    // Remove from local list immediately for instant feedback
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
  };

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar forceSolid />
        <main className="pt-24 pb-20 px-4 md:px-10">
          <div className="max-w-7xl mx-auto text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-50 to-amber-100 border-2 border-orange-200 flex items-center justify-center">
                <i className="ri-bookmark-line text-3xl text-orange-500"></i>
              </div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
                Việc làm đã lưu
              </h1>
              <p className="text-foreground-600 text-sm mb-8 leading-relaxed">
                Đăng nhập để xem danh sách việc làm bạn đã lưu. Tính năng này giúp bạn dễ dàng quay lại các cơ hội việc làm yêu thích.
              </p>
              <Link
                to="/dang-nhap"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-base font-bold hover:from-orange-600 hover:to-amber-600 transition-all duration-200 no-underline cursor-pointer whitespace-nowrap shadow-[0_4px_20px_rgba(251,146,60,0.35)]"
              >
                <i className="ri-login-box-line text-lg"></i>
                Đăng nhập / Đăng ký ngay
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar forceSolid />
      <main className="pt-24 pb-20 px-4 md:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-10 md:mb-12">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-accent-100/80 flex items-center justify-center">
                <i className="ri-bookmark-fill text-lg text-accent-600"></i>
              </div>
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
                Việc làm đã lưu
              </h1>
            </div>
            <p className="text-foreground-600 text-sm md:text-base ml-[52px]">
              {savedCount > 0
                ? `Bạn đã lưu ${savedCount} việc làm. Nhấn vào để xem chi tiết và ứng tuyển.`
                : "Danh sách việc làm bạn đã bookmark để xem sau."}
            </p>
          </div>

          {/* Loading */}
          {(loading || savedLoading) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 p-5 animate-pulse">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-lg bg-background-200/70"></div>
                      <div>
                        <div className="h-4 w-36 bg-background-200/70 rounded mb-2"></div>
                        <div className="h-3 w-24 bg-background-200/70 rounded"></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <div className="h-6 w-20 bg-background-200/70 rounded-full"></div>
                    <div className="h-6 w-24 bg-background-200/70 rounded-full"></div>
                  </div>
                  <div className="h-4 w-full bg-background-200/70 rounded mb-3"></div>
                  <div className="h-4 w-3/4 bg-background-200/70 rounded mb-4"></div>
                  <div className="flex justify-between">
                    <div className="h-3 w-20 bg-background-200/70 rounded"></div>
                    <div className="h-8 w-28 bg-background-200/70 rounded-full"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {!loading && !savedLoading && error && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-500"></i>
              </div>
              <p className="text-foreground-700 font-medium mb-4">{error}</p>
              <button
                onClick={fetchJobDetails}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                <span>Thử lại</span>
              </button>
            </div>
          )}

          {/* Empty state */}
          {!loading && !savedLoading && !error && jobs.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-200/70 flex items-center justify-center">
                <i className="ri-bookmark-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                Chưa có việc làm nào được lưu
              </h3>
              <p className="text-sm text-foreground-500 mb-8 max-w-md mx-auto leading-relaxed">
                Khi bạn thấy việc làm phù hợp, hãy nhấn vào biểu tượng <i className="ri-bookmark-line text-accent-500"></i> để lưu lại và xem sau.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors no-underline cursor-pointer whitespace-nowrap"
              >
                <i className="ri-briefcase-line text-base"></i>
                Xem việc làm mới nhất
              </Link>
            </div>
          )}

          {/* Job cards */}
          {!loading && !savedLoading && !error && jobs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {jobs.map((job, index) => (
                <div
                  key={job.id}
                  className="opacity-0 animate-card-enter"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <div className="bg-background-50 rounded-xl border-2 border-orange-400/50 overflow-hidden transition-all duration-400 group hover:border-orange-400 hover:shadow-[0_0_18px_rgba(251,146,60,0.12)]">
                    <div className="p-5 md:p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 md:w-12 md:h-12 rounded-lg bg-primary-100/70 flex items-center justify-center flex-shrink-0">
                            <i className="ri-building-line text-primary-600 text-lg md:text-xl"></i>
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-heading font-semibold text-base text-foreground-900 truncate">
                              {job.title}
                            </h3>
                            <p className="text-sm text-foreground-600 truncate">
                              {job.company}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => handleUnsave(job.id)}
                            className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-600 hover:bg-accent-200/80 transition-all duration-200 cursor-pointer"
                            title="Bỏ lưu việc làm này"
                          >
                            <i className="ri-bookmark-fill text-lg"></i>
                          </button>
                          {job.isHot && (
                            <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full whitespace-nowrap">
                              HOT
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1 text-xs text-foreground-600 bg-secondary-100/70 px-2.5 py-1 rounded-full">
                          <i className="ri-map-pin-line text-xs"></i>
                          {job.location}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-accent-700 bg-accent-100/70 px-2.5 py-1 rounded-full font-medium">
                          <i className="ri-money-dollar-circle-line text-xs"></i>
                          {job.salary}
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-foreground-500 bg-background-200/70 px-2.5 py-1 rounded-full">
                          <i className="ri-briefcase-4-line text-xs"></i>
                          {typeLabels[job.type] || job.type}
                        </span>
                      </div>

                      <p className="text-sm text-foreground-700 leading-relaxed line-clamp-2 mb-4">
                        {job.description}
                      </p>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-400">
                          <i className="ri-bookmark-line mr-1"></i>
                          Đã lưu {new Date(job.savedAt).toLocaleDateString("vi-VN")}
                        </span>
                        <Link
                          to="/"
                          className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-200 no-underline cursor-pointer whitespace-nowrap"
                        >
                          <span>Xem chi tiết</span>
                          <i className="ri-arrow-right-line text-base"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}